/**
 * Weather Types for Bona Pisca
 * Comprehensive weather data types for fishing conditions
 */

export interface MarineSnapshot {
  seaSurfaceTemperature?: number | null;
  waveHeight?: number | null;
  wavePeriod?: number | null;
  waveDirection?: number | null;
  oceanCurrentVelocity?: number | null;
  oceanCurrentDirection?: number | null;
  seaLevelHeight?: number | null;
}

export interface FishingAssessmentFactor {
  score: number;
  label: string;
  reason: string;
}

export interface FishingAssessment {
  overall: number;
  verdict: 'good' | 'fair' | 'poor';
  headline: string;
  summary: string;
  confidence: 'high' | 'medium' | 'low';
  bestWindow: {
    label: string;
    start: string;
    end: string;
  } | null;
  factors: {
    safety: FishingAssessmentFactor;
    activity: FishingAssessmentFactor;
    water: FishingAssessmentFactor;
    tideCurrent: FishingAssessmentFactor;
    solunar: FishingAssessmentFactor;
  };
  tide: {
    currentLevel: number;
    movement: 'rising' | 'falling' | 'slack';
    range: number;
    nextExtremeTime: string | null;
    nextExtremeType: 'high' | 'low' | null;
  };
  solunar: {
    dailyScore: number;
    currentPeriod: 'major' | 'minor' | 'background';
    nextPeak: string | null;
  };
  warnings: string[];
  recommendations: string[];
}

export interface WeatherConditions {
  temperature: number;
  feelsLike: number;
  tempMin: number;
  tempMax: number;
  humidity: number;
  pressure: number;
  windSpeed: number;
  windDirection: number;
  windGust?: number;
  visibility: number;
  cloudCover: number;
  condition: WeatherCondition;
  description: string;
  icon: string;
  seaLevel?: number;
  groundLevel?: number;
  rain1h?: number;
  rain3h?: number;
  snow1h?: number;
  snow3h?: number;
  sunrise: Date;
  sunset: Date;
  location: {
    name: string;
    country: string;
    lat: number;
    lon: number;
  };
  timestamp: Date;
  timezone: number;
  marine?: MarineSnapshot;
  fishingAssessment?: FishingAssessment;
}

export interface WeatherForecast {
  timestamp: Date;
  temperature: number;
  tempMin: number;
  tempMax: number;
  condition: WeatherCondition;
  description: string;
  icon: string;
  windSpeed: number;
  windDirection: number;
  windGust?: number;
  cloudCover: number;
  humidity: number;
  pressure: number;
  rain?: number;
  snow?: number;
  pop: number;
}

export interface DailyForecast {
  date: Date;
  tempMin: number;
  tempMax: number;
  condition: WeatherCondition;
  description: string;
  icon: string;
  windSpeed: number;
  windDirection: number;
  humidity: number;
  pressure: number;
  rain?: number;
  snow?: number;
  pop: number;
  sunrise: Date;
  sunset: Date;
}

export type WeatherCondition =
  | 'clear'
  | 'clouds'
  | 'rain'
  | 'drizzle'
  | 'thunderstorm'
  | 'snow'
  | 'mist'
  | 'fog'
  | 'dust'
  | 'sand'
  | 'ash'
  | 'squall'
  | 'tornado';
export interface FishingScore {
  overall: number; // 0-100
  factors: {
    wind: number; // 0-100
    temperature: number; // 0-100
    pressure: number; // 0-100
    cloudCover: number; // 0-100
    precipitation: number; // 0-100
    visibility: number; // 0-100
  };
  recommendation: 'excellent' | 'good' | 'fair' | 'poor' | 'dangerous';
  warnings: WeatherWarning[];
  tips: string[];
}

export interface WeatherWarning {
  type: 'wind' | 'storm' | 'visibility' | 'temperature' | 'rain' | 'waves';
  severity: 'low' | 'medium' | 'high' | 'extreme';
  message: string;
  localizedMessage?: string;
}

// OpenWeatherMap API Response Types
export interface OpenWeatherResponse {
  coord: {
    lon: number;
    lat: number;
  };
  weather: Array<{
    id: number;
    main: string;
    description: string;
    icon: string;
  }>;
  base: string;
  main: {
    temp: number;
    feels_like: number;
    temp_min: number;
    temp_max: number;
    pressure: number;
    humidity: number;
    sea_level?: number;
    grnd_level?: number;
  };
  visibility: number;
  wind: {
    speed: number;
    deg: number;
    gust?: number;
  };
  clouds: {
    all: number;
  };
  rain?: {
    '1h'?: number;
    '3h'?: number;
  };
  snow?: {
    '1h'?: number;
    '3h'?: number;
  };
  dt: number;
  sys: {
    country: string;
    sunrise: number;
    sunset: number;
  };
  timezone: number;
  id: number;
  name: string;
  cod: number;
}

export interface OpenWeatherForecastResponse {
  cod: string;
  message: number;
  cnt: number;
  list: Array<{
    dt: number;
    main: {
      temp: number;
      feels_like: number;
      temp_min: number;
      temp_max: number;
      pressure: number;
      sea_level: number;
      grnd_level: number;
      humidity: number;
    };
    weather: Array<{
      id: number;
      main: string;
      description: string;
      icon: string;
    }>;
    clouds: {
      all: number;
    };
    wind: {
      speed: number;
      deg: number;
      gust?: number;
    };
    visibility: number;
    pop: number;
    rain?: {
      '3h': number;
    };
    snow?: {
      '3h': number;
    };
    dt_txt: string;
  }>;
  city: {
    id: number;
    name: string;
    coord: {
      lat: number;
      lon: number;
    };
    country: string;
    timezone: number;
    sunrise: number;
    sunset: number;
  };
}

// Beaufort Scale for wind (important for marine conditions)
export const BeaufortScale = [
  { force: 0, speed: 0.3, description: 'calm', seaCondition: 'mirror-like' },
  { force: 1, speed: 1.6, description: 'light-air', seaCondition: 'ripples' },
  { force: 2, speed: 3.4, description: 'light-breeze', seaCondition: 'small-wavelets' },
  { force: 3, speed: 5.5, description: 'gentle-breeze', seaCondition: 'large-wavelets' },
  { force: 4, speed: 8.0, description: 'moderate-breeze', seaCondition: 'small-waves' },
  { force: 5, speed: 10.8, description: 'fresh-breeze', seaCondition: 'moderate-waves' },
  { force: 6, speed: 13.9, description: 'strong-breeze', seaCondition: 'large-waves' },
  { force: 7, speed: 17.2, description: 'near-gale', seaCondition: 'heaping-sea' },
  { force: 8, speed: 20.8, description: 'gale', seaCondition: 'moderately-high-waves' },
  { force: 9, speed: 24.5, description: 'strong-gale', seaCondition: 'high-waves' },
  { force: 10, speed: 28.5, description: 'storm', seaCondition: 'very-high-waves' },
  { force: 11, speed: 32.7, description: 'violent-storm', seaCondition: 'exceptionally-high-waves' },
  { force: 12, speed: 999, description: 'hurricane', seaCondition: 'phenomenal-waves' }
] as const;

// Wind direction helpers
export const getWindDirection = (degrees: number): string => {
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const index = Math.round(degrees / 22.5) % 16;
  return directions[index];
};

export const getBeaufortForce = (windSpeed: number): typeof BeaufortScale[number] => {
  for (let i = BeaufortScale.length - 1; i >= 0; i--) {
    if (windSpeed >= BeaufortScale[i].speed) {
      return BeaufortScale[i];
    }
  }
  return BeaufortScale[0];
};